import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { ComparisonDimension } from '../../../services/benchmarkService';
import { QualifiedBadge } from '../BenchmarkQualifiers';
import { count, percent } from '../BenchmarkVocabulary';
import { WithheldIndex } from '../withheldMetrics';
import { SIDE_LABELS, coverageGapLine, dimensionQualifications, flagOverlap, metric, producerLabel } from './investigationVocabulary';
import { dsVar, ink } from '../../../theme/tokens';

export interface ScorecardRow {
    dimension: ComparisonDimension;
    /** 1 for a factor listed under its agent. */
    depth?: number;
}

interface DimensionScorecardProps {
    rows: ScorecardRow[];
    withheld: WithheldIndex;
    selectedKey?: string | null;
    onSelect: (dimensionKey: string) => void;
    /** Show who produces each row; off where the grouping already says. */
    showProducer?: boolean;
    emptyMessage?: string;
}

const headSx = {
    textAlign: 'left' as const, px: 1.5, py: 1.25, fontSize: '0.6875rem', fontWeight: 600,
    letterSpacing: '0.05em', textTransform: 'uppercase' as const, color: dsVar('fg-muted'), whiteSpace: 'nowrap' as const
};
const cellSx = { px: 1.5, py: 1.1, fontSize: '0.8125rem', color: dsVar('fg-soft'), verticalAlign: 'middle', fontVariantNumeric: 'tabular-nums' };

const HEADER_TIPS: Record<string, string> = {
    [SIDE_LABELS.platformOnly]: 'Cases the agent replay flagged and the historical record did not.',
    [SIDE_LABELS.institutionOnly]: 'Cases the historical record flagged and the agent replay did not.',
    Both: 'Cases both sides flagged.',
    Agreement: 'Of the comparable cases, the share where both sides gave the same answer, including the cases both cleared. High for any rare red flag, since most cases are cleared by both.',
    'Flag overlap': 'Of the cases either side flagged, the share both flagged: Both ÷ (Replay only + Historical only + Both). Cases both cleared are left out, so a rare red flag cannot look well matched just because both sides mostly said no.'
};

/** A count that is zero reads as a dot, so the non-zero disagreements stand out. */
const Count: React.FC<{ n: number | undefined; color: string }> = ({ n, color }) => (
    <Box component="span" sx={{ color: n ? color : dsVar('fg-faint'), fontWeight: n ? 700 : 400 }}>{n ? count(n) : '·'}</Box>
);

/**
 * One row per compared dimension: how many cases could be compared, where the
 * two sides agreed and disagreed, and how much their flags overlap. A row opens
 * the dimension in full.
 *
 * Overlap rather than Cohen's kappa: kappa's −1 to 1 against a chance model
 * cannot be checked from the row, while overlap is read straight off its counts
 * and makes the same point for a rare red flag. Kappa stays in the item's figures.
 *
 * Coverage leads, because a dimension compared on 3 of 100 cases says almost
 * nothing however high its agreement — and a dimension compared on none says
 * why it is empty instead of showing a perfect score.
 */
const DimensionScorecard: React.FC<DimensionScorecardProps> = ({
    rows, withheld, selectedKey, onSelect, showProducer = false, emptyMessage = 'Nothing was compared here.'
}) => {
    const columns = ['Compared', ...(showProducer ? ['Produced by'] : []), 'Coverage', SIDE_LABELS.platformOnly, SIDE_LABELS.institutionOnly, 'Both', 'Agreement', 'Flag overlap', ''];
    return (
        <Box sx={{ border: `1px solid ${dsVar('line')}`, borderRadius: '8px', backgroundColor: dsVar('surface'), overflowX: 'auto' }}>
            <Box component="table" sx={{ width: '100%', minWidth: 880, borderCollapse: 'collapse' }}>
                <Box component="thead">
                    <Box component="tr" sx={{ borderBottom: `1px solid ${dsVar('line')}` }}>
                        {columns.map((c, i) => (
                            <Box key={`${c}-${i}`} component="th" sx={headSx}>
                                {HEADER_TIPS[c] ? <Tooltip title={HEADER_TIPS[c]} arrow describeChild><span>{c}</span></Tooltip> : c}
                            </Box>
                        ))}
                    </Box>
                </Box>
                <Box component="tbody">
                    {rows.length === 0 && (
                        <Box component="tr">
                            <Box component="td" colSpan={columns.length} sx={{ p: 5, textAlign: 'center', color: dsVar('fg-muted'), fontSize: '0.875rem' }}>
                                {emptyMessage}
                            </Box>
                        </Box>
                    )}
                    {rows.map(({ dimension, depth = 0 }) => {
                        const selected = dimension.dimensionKey === selectedKey;
                        const agreement = metric(dimension.metrics, 'agreementRate');
                        const overlap = flagOverlap(dimension.agreementCounts);
                        const counts = dimension.agreementCounts;
                        const none = dimension.comparable === 0;
                        const gaps = coverageGapLine(dimension);
                        const coverageTip = (none
                            ? 'No case could be compared: one side recorded nothing for each.'
                            : `${count(dimension.comparable)} of ${count(dimension.subjects)} cases had both sides recorded.`)
                            + (gaps ? ` Not compared: ${gaps}.` : '');
                        return (
                            <Box
                                key={dimension.dimensionKey}
                                component="tr"
                                data-dimension-key={dimension.dimensionKey}
                                onClick={() => onSelect(dimension.dimensionKey)}
                                aria-selected={selected}
                                sx={{
                                    borderTop: `1px solid ${dsVar('line')}`, cursor: 'pointer',
                                    backgroundColor: selected ? dsVar('raised') : depth ? dsVar('page', 0.35) : 'transparent',
                                    boxShadow: selected ? `inset 2px 0 0 ${dsVar('accent-fg')}` : 'none',
                                    '&:hover': { backgroundColor: dsVar('raised') }
                                }}
                            >
                                <Box component="td" sx={{ ...cellSx, pl: depth ? 4 : 1.5 }}>
                                    <Typography sx={{ fontSize: depth ? '0.8125rem' : '0.875rem', fontWeight: depth ? 500 : 700, color: dsVar('fg-body') }}>
                                        {dimension.label}
                                    </Typography>
                                    {dimension.valueKind !== 'binary' && dimension.flagAt !== null && (
                                        <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted') }}>
                                            {dimension.valueKind} · flags at {dimension.flagAt}
                                        </Typography>
                                    )}
                                </Box>
                                {showProducer && <Box component="td" sx={{ ...cellSx, color: dsVar('fg-2'), fontSize: '0.75rem' }}>{dimension.producerLabel ?? producerLabel(dimension.producer)}</Box>}
                                <Box component="td" sx={cellSx}>
                                    <Tooltip arrow title={coverageTip}>
                                        <Box component="span" sx={{ color: none ? ink('#FBBF24') : dsVar('fg-soft') }}>
                                            {count(dimension.comparable)} / {count(dimension.subjects)}
                                        </Box>
                                    </Tooltip>
                                </Box>
                                <Box component="td" sx={cellSx}><Count n={counts.platform_only} color="#F97316" /></Box>
                                <Box component="td" sx={cellSx}><Count n={counts.institution_only} color="#0EA5E9" /></Box>
                                <Box component="td" sx={cellSx}><Count n={counts.both_flagged} color="#10B981" /></Box>
                                <Box component="td" sx={cellSx}>{none ? '—' : percent(agreement?.value, 0)}</Box>
                                <Box component="td" sx={cellSx}>
                                    {none ? '—' : !overlap ? (
                                        <Tooltip arrow title="Neither side flagged a comparable case, so there is no overlap to measure.">
                                            <Box component="span" sx={{ color: dsVar('fg-faint') }}>—</Box>
                                        </Tooltip>
                                    ) : (
                                        <Tooltip arrow title={`${count(overlap.both)} of the ${count(overlap.either)} cases either side flagged were flagged by both.`}>
                                            <Box component="span">
                                                {percent(overlap.value, 0)}
                                                <Box component="span" sx={{ color: dsVar('fg-muted'), fontSize: '0.6875rem', ml: 0.75 }}>
                                                    {count(overlap.both)} of {count(overlap.either)}
                                                </Box>
                                            </Box>
                                        </Tooltip>
                                    )}
                                </Box>
                                <Box component="td" sx={{ ...cellSx, textAlign: 'right' }}>
                                    <QualifiedBadge qualifications={dimensionQualifications(withheld, dimension)} />
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </Box>
    );
};

export default DimensionScorecard;
